import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { lateApi, employeeApi } from '../lib/api';
// `allowedUnits` only — the units this user may work with, straight from the
// /units master. The header's own selection is deliberately NOT read here:
// this page is driven by its own dropdown, and nothing else.
import { useUnit } from '../contexts/UnitContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const HR_MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
function fyNow() {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
}
const today = () => new Date().toISOString().slice(0, 10);
const monthNow = () => HR_MONTHS[(new Date().getMonth() + 9) % 12];

export default function LateComing() {
  const fy = fyNow();
  // The workspace decides the scope. A unit workspace IS the unit filter, so
  // the page shows no second control for it — there is nothing to choose.
  const { activeUnit, allowedUnits, isAllUnits } = useUnit();
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  // add-entry form. There is no unit here on purpose: the employee carries it.
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState('');
  const [empId, setEmpId] = useState('');
  const [inPunch, setInPunch] = useState('');
  const [lateMin, setLateMin] = useState('');
  const [remark, setRemark] = useState('');
  // The company's penalty bands, fetched rather than written out here: the
  // server owns the table and applies it on save, so a copy in this file could
  // drift and show HR a figure the register would not store.
  const [latePolicy, setLatePolicy] = useState(null);
  // True once HR edits Late (min) by hand, which stops the punch overwriting
  // their figure — an approved late arrival has to be able to keep its own.
  const [lateEdited, setLateEdited] = useState(false);

  // monthly summary
  const [month, setMonth] = useState(monthNow());
  const [summary, setSummary] = useState(null);

  // Narrowing WITHIN the Corporate HQ workspace, and meaningless outside it:
  // in a unit workspace the header has already decided, so this stays '' and
  // its dropdown is never rendered.
  const [pick, setPick] = useState('');

  // The unit this page is looking at, in one expression. Declared HERE, above
  // every hook that reads it — a `const` used earlier in the render would be
  // in its temporal dead zone and crash the page.
  //   unit workspace  -> that unit, always, with no way to change it here
  //   Corporate HQ    -> whatever the in-page filter says ('' = every unit)
  const unit = isAllUnits ? pick : (activeUnit || '');

  // Scoped to the unit chosen in the dropdown below — the one control on this
  // page. Refetched when it changes, so switching to Corporate Office cannot
  // leave Unit 3 names behind in the picker. On Corporate HQ nothing is loaded
  // at all: that view files nothing, so it needs no candidates.
  //
  // Convenience only. The backend re-reads the employee's own unit on save and
  // refuses anyone outside the caller's scope, whatever the browser sends.
  useEffect(() => {
    if (!unit) { setEmps([]); setQ(''); setEmpId(''); return; }
    setQ(''); setEmpId('');          // never carry a name across a unit change
    employeeApi.list({ unit, limit: 3000 })
      .then((r) => setEmps(Array.isArray(r.data) ? r.data : (r.data?.employees || [])))
      .catch(() => toast.error('Could not load employee list'));
  }, [unit]);

  // Fetched once. If it cannot be loaded the punch simply stops pre-filling the
  // slab and HR types it as before — the server still applies the band table on
  // save, so a failed fetch here cannot put a wrong figure in the register.
  useEffect(() => {
    lateApi.policy().then((r) => setLatePolicy(r.data)).catch(() => setLatePolicy(null));
  }, []);

  /** The penalty band a punch falls into, straight from the server's table. */
  const slabFor = useCallback((punch) => {
    if (!latePolicy || !/^\d{2}:\d{2}$/.test(punch || '')) return null;
    const mins = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const p = mins(punch);
    // Grace runs THROUGH grace_until inclusive — 08:40 on the dot is on time.
    if (p <= mins(latePolicy.grace_until)) return 0;
    for (const b of latePolicy.bands || []) if (p <= mins(b.until)) return b.minutes;
    // Past the last band. Not less late than the band below it.
    const last = (latePolicy.bands || []).slice(-1)[0];
    return last ? last.minutes : null;
  }, [latePolicy]);

  // Typing the punch fills Late (min). HR can still overtype it, and once they
  // do this leaves their number alone.
  useEffect(() => {
    if (lateEdited) return;
    const slab = slabFor(inPunch);
    setLateMin(slab == null ? '' : String(slab));
  }, [inPunch, slabFor, lateEdited]);

  const loadDay = useCallback(async () => {
    try { const r = await lateApi.byDate(date); setRows(r.data.rows || []); }
    catch (e) { toast.error('Failed to load entries'); }
  }, [date]);
  useEffect(() => { loadDay(); }, [loadDay]);

  // ---- Sync Now -----------------------------------------------------------
  // The website cannot reach the punch machine: it is on the office network
  // and this runs in the cloud. So this button fetches nothing itself. It
  // raises a flag, the office agent (already polling) sees it, pulls the
  // punches and pushes them through the EXISTING ingest; this page watches for
  // the completion stamp and then re-reads the day.
  //
  // Everything here is local to the button. The rest of the page stays usable
  // while it waits — HR can keep typing an entry, change the date, or leave.
  const [sync, setSync] = useState('');        // '' | 'waiting' | 'done' | 'timeout'
  const syncTimer = useRef(null);
  const syncStop = useRef(null);

  // Polling must not outlive the page, or it writes state into a component
  // that is gone — and on a 90-second window that is easy to do by navigating.
  useEffect(() => () => {
    clearInterval(syncTimer.current);
    clearTimeout(syncStop.current);
  }, []);

  const syncNow = async () => {
    clearInterval(syncTimer.current);
    clearTimeout(syncStop.current);
    let requestedAt;
    try {
      const r = await lateApi.syncRequest();
      requestedAt = r.data?.requested_at;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not ask the office PC to sync');
      return;
    }
    setSync('waiting');

    const finish = (state) => {
      clearInterval(syncTimer.current);
      clearTimeout(syncStop.current);
      setSync(state);
      // The outcome clears itself; leaving "Updated ✓" on screen would still
      // be claiming it an hour later.
      syncStop.current = setTimeout(() => setSync(''), state === 'done' ? 4000 : 9000);
    };

    syncTimer.current = setInterval(async () => {
      try {
        const { data } = await lateApi.syncStatus();
        // Compared against the stamp THIS click got back, not merely "not
        // pending": an ingest that finished before the click would otherwise
        // read as this click's answer.
        if (data?.completed_at && requestedAt && data.completed_at >= requestedAt) {
          await loadDay();
          finish('done');
        }
      } catch { /* a dropped poll is not a failure — the next one retries */ }
    }, 3000);

    syncStop.current = setTimeout(() => finish('timeout'), 90000);
  };

  const loadMonth = async () => {
    try { const r = await lateApi.byMonth(fy, month); setSummary(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load month'); }
  };

  // The units this user may work with, from the /units master via UnitContext,
  // plus any unit already visible in the loaded data. The master is the stable
  // part — the picker empties on Corporate HQ, so deriving the list from
  // employees alone would make the dropdown collapse to one option the moment
  // you selected it. The union means a unit can never go missing while a row
  // using it is on screen. No hardcoded list, no second unit master.
  const units = useMemo(() => {
    const seen = new Set((allowedUnits || []).filter((u) => u && u !== 'All Units'));
    [...emps, ...rows, ...((summary && summary.summary) || [])]
      .forEach((x) => { const u = (x && x.unit || '').trim(); if (u) seen.add(u); });
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [allowedUnits, emps, rows, summary]);

  // True only for the combined, read-only view. A unit workspace is never
  // "HQ", so filing stays available there exactly as before.
  const isHQ = !unit;

  const inUnit = (x) => !unit || ((x && x.unit) || '') === unit;

  // Every typed word must match somewhere in the name ("dee kau" finds DEEPAK KAUSHIK).
  //
  // Only the typed words are applied here: `emps` was already fetched for the
  // chosen entry unit. Applying the DISPLAY filter as well would narrow the
  // picker twice — filing into Corporate Office while the register below is
  // filtered to Unit 1 would offer nobody at all.
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = terms.length
    ? emps.filter((e) => terms.every((t) => (e.name || '').toLowerCase().includes(t))).slice(0, 8)
    : [];
  const selEmp = emps.find((e) => e.id === empId);
  const visibleRows = rows.filter(inUnit);
  const visibleSummary = ((summary && summary.summary) || []).filter(inUnit);

  const addEntry = async () => {
    if (!empId) { toast.error('Type the name and CLICK the employee in the dropdown list first'); return; }
    // In punch is the evidence behind the entry, and leaving it out is silent:
    // the server accepts a blank, the row still counts towards the KPI, and the
    // register simply shows a dash for ever — there is no other record of the
    // time to recover it from. 20 entries over 3 and 5 Oct 2026 were filed this
    // way before this guard existed. Asked for here rather than enforced in the
    // API, so an older client or an import is not broken by it.
    if (!inPunch) { toast.error('Enter the In punch time — it cannot be filled in later'); return; }
    // Said here as well as on the server so HR is told while the form is still
    // in front of them. The server is the rule; this only saves the round trip.
    if (slabFor(inPunch) === 0) {
      toast.error(`On time — anyone punching in by ${latePolicy?.grace_until} is `
                  + 'within the grace period, so there is no late entry to record');
      return;
    }
    setBusy(true);
    try {
      // No unit is sent. The server reads it off the employee's own record, so
      // there is nothing here that could file somebody under the wrong plant.
      await lateApi.add({ employee_id: empId, date, in_punch: inPunch,
        late_minutes: lateMin === '' ? null : Number(lateMin), remark });
      toast.success('Entry saved');
      setQ(''); setEmpId(''); setInPunch(''); setLateMin(''); setRemark('');
      setLateEdited(false);            // the next punch auto-fills again
      await loadDay();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const removeEntry = async (r) => {
    if (!window.confirm(`Remove ${r.name} — ${r.date}?`)) return;
    try { await lateApi.remove(r.id); await loadDay(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-[#172554]">Late Coming Register</h1>
            <p className="text-sm text-[#5A6377]">Daily entries by HR · month total auto-fills each employee's LATE COMING KPI ({fy}). Scoring: 0 late = 4 marks, 1–2 = 3, 3–4 = 2, 5–6 = 1, more than 6 = 0.</p>
          </div>

          {/* Daily entry sheet */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h3 className="font-medium text-[#11182B]">Entries for</h3>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="border border-[#E6E9F0] rounded-lg px-2.5 py-1.5 text-sm" data-testid="late-date" />
              {/* Pulls today's punches from the office machine on demand.
                  Named for what it does to the register, not for the plumbing:
                  nobody filing a late entry needs to know an agent is
                  involved. The outcome line beside it does say so, because
                  "nothing came back" is only actionable if you know a PC in
                  the office has to be on. */}
              <Button size="sm" variant="outline" onClick={syncNow}
                disabled={sync === 'waiting'} data-testid="late-sync-now">
                {sync === 'waiting' ? 'Syncing…' : 'Sync Now'}
              </Button>
              {sync === 'waiting' && (
                <span className="flex items-center gap-1.5 text-xs text-[#5A6377]"
                  data-testid="late-sync-waiting">
                  <span className="inline-block h-3 w-3 rounded-full border-2
                                   border-[#C9CFDC] border-t-[#2563EB] animate-spin" />
                  Syncing… fetching from the office machine
                </span>
              )}
              {sync === 'done' && (
                <span className="text-xs font-semibold text-[#15803D]"
                  data-testid="late-sync-done">Updated ✓</span>
              )}
              {sync === 'timeout' && (
                <span className="text-xs text-[#B14A0E]" data-testid="late-sync-timeout">
                  Office PC se jawab nahi aaya — make sure the office PC is ON and on the
                  office network, then try again
                </span>
              )}
              {/* Only in the Corporate HQ workspace. In a unit workspace the
                  header has already chosen, and offering a second control
                  would imply you could look at a unit you are not in. */}
              {isAllUnits && (
                <label className="flex items-center gap-1.5">
                  <span className="text-xs text-[#5A6377]">Unit</span>
                  <select value={pick} onChange={(e) => setPick(e.target.value)}
                    data-testid="late-unit-filter"
                    className="border border-[#E6E9F0] rounded-lg px-2.5 py-1.5 text-sm">
                    <option value="">Corporate HQ — All Units</option>
                    {units.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
              )}
              {!isAllUnits && (
                <span className="text-xs text-[#5A6377]" data-testid="late-unit-fixed">
                  Unit <b className="text-[#172554]">{activeUnit}</b>
                </span>
              )}
              <span className="text-xs text-[#98A1B2]" data-testid="late-count">
                {visibleRows.length} entr{visibleRows.length === 1 ? 'y' : 'ies'}
                {unit && rows.length !== visibleRows.length && ` of ${rows.length}`}
              </span>
              {isAllUnits && pick && (
                <button onClick={() => setPick('')} data-testid="late-unit-clear"
                  className="text-xs text-[#2563EB] hover:underline">Corporate HQ view</button>
              )}
            </div>

            {/* Corporate HQ: every unit's records, read only. */}
            {isHQ && (
              <div className="mb-4 border border-[#D7E3FF] bg-[#F5F8FF] rounded-lg p-3 flex
                              items-start gap-2" data-testid="late-hq-readonly">
                <span className="text-[#2563EB] text-sm leading-none mt-0.5">🏢</span>
                <div className="text-[12px] text-[#11182B]">
                  <b>Corporate HQ — all units.</b>{' '}
                  <span className="text-[#5A6377]">
                    Showing late-coming records from every unit you are authorised to see.
                    To add an entry, pick that employee&rsquo;s unit in the Unit dropdown
                    above, or open that unit&rsquo;s workspace — an entry always
                    belongs to one unit.
                  </span>
                </div>
              </div>
            )}

            {/* add row — unit mode only */}
            {!isHQ && (
            <div className="flex flex-wrap items-start gap-2 mb-4 border border-[#F2F4F8] rounded-lg p-3 bg-[#FAFBFD]">
              <div className="relative">
                <span className="block text-[10px] uppercase tracking-wide text-[#98A1B2] mb-0.5">Employee</span>
                <Input value={selEmp ? selEmp.name : q} placeholder="Employee name…"
                  className="w-52 text-sm"
                  onChange={(e) => { setQ(e.target.value); setEmpId(''); }} data-testid="late-emp-search" />
                {!empId && terms.length > 0 && (
                  <div className="absolute z-20 mt-1 w-72 bg-white border border-[#E6E9F0] rounded-lg shadow-md max-h-56 overflow-auto">
                    {filtered.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-[#C2151F]">
                        No employee named “{q}” — check the spelling and pick from the list.
                      </div>
                    ) : filtered.map((e) => (
                      <button key={e.id} onClick={() => { setEmpId(e.id); setQ(''); }}
                        className="block w-full text-left px-3 py-1.5 text-sm hover:bg-[#F2F4F8]">
                        {e.name} <span className="text-[11px] text-[#98A1B2]">{e.department} · {e.unit}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Read-only confirmation of where this entry will land. Shown
                  because the unit is now decided for you — seeing it is how
                  you catch picking the wrong person of the same name. */}
              {selEmp && (
                <div className="text-xs text-[#5A6377] py-2" data-testid="late-emp-unit">
                  {selEmp.department || '—'} · {selEmp.designation || '—'} ·{' '}
                  <span className="font-semibold text-[#172554]">{selEmp.unit || '— no unit —'}</span>
                </div>
              )}
              {/* A clock, not a text box: typing "9.42am" or "0942" used to be
                  accepted and then read back differently everywhere. */}
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-[#98A1B2] mb-0.5">In punch</span>
                <Input type="time" value={inPunch} onChange={(e) => setInPunch(e.target.value)}
                  className="w-32 text-sm" data-testid="late-in-punch" />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-[#98A1B2] mb-0.5">
                  Late (min)
                  {!lateEdited && lateMin !== '' && (
                    <span className="ml-1 normal-case tracking-normal text-[#15654B]">· auto</span>
                  )}
                </span>
                <Input type="number" min="0" value={lateMin} data-testid="late-minutes"
                  onChange={(e) => { setLateEdited(true); setLateMin(e.target.value); }}
                  placeholder="e.g. 12" className="w-24 text-sm" />
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-[#98A1B2] mb-0.5">Remark</span>
                <Input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Optional" className="w-44 text-sm" />
              </label>
              <Button size="sm" onClick={addEntry} disabled={busy} data-testid="late-add" className="mt-4">Add</Button>
            </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                  <tr>
                    <th className="py-2 px-2 text-left">Name</th><th className="py-2 px-2 text-left">Department</th>
                    <th className="py-2 px-2 text-left">Designation</th><th className="py-2 px-2 text-left">Unit</th>
                    <th className="py-2 px-2 text-center">Date</th><th className="py-2 px-2 text-center">In punch</th>
                    <th className="py-2 px-2 text-center">Late (min)</th><th className="py-2 px-2 text-left">Remark</th>
                    <th className="py-2 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={9} className="py-6 text-center text-[#98A1B2]">
                      {rows.length === 0
                        ? 'No late entries for this date.'
                        : `No late entries for this date in ${unit}.`}
                    </td></tr>
                  )}
                  {visibleRows.map((r) => (
                    <tr key={r.id} className="border-b border-[#F2F4F8]">
                      <td className="py-1.5 px-2 text-[#11182B]">{r.name}</td>
                      <td className="py-1.5 px-2 text-[#5A6377]">{r.department || '—'}</td>
                      <td className="py-1.5 px-2 text-[#5A6377]">{r.designation || '—'}</td>
                      <td className="py-1.5 px-2 text-[#5A6377]">{r.unit || '—'}</td>
                      <td className="py-1.5 px-2 text-center">{r.date}</td>
                      <td className="py-1.5 px-2 text-center">{r.in_punch || '—'}</td>
                      <td className="py-1.5 px-2 text-center font-semibold text-[#C2151F]">{r.late_minutes ?? '—'}</td>
                      <td className="py-1.5 px-2 text-[#5A6377]">{r.remark || ''}</td>
                      {/* Read-only at Corporate HQ: the brief calls this a
                          view/fetch screen, so it offers no way to change a
                          record either. Deleting stays in the owning unit. */}
                      <td className="py-1.5 px-2 text-right">
                        {!isHQ && (
                          <button onClick={() => removeEntry(r)} data-testid={`late-del-${r.id}`}
                            className="text-[#C2151F] text-xs">✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly summary -> what the KPI will get */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-medium text-[#11182B]">Monthly summary → LATE COMING KPI</h3>
              <select value={month} onChange={(e) => setMonth(e.target.value)}
                className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm">
                {HR_MONTHS.map((m) => <option key={m} value={m}>{m} {fy}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={loadMonth} data-testid="late-month-load">Load</Button>
            </div>
            {summary && (
              visibleSummary.length === 0 ? (
                <p className="text-sm text-[#98A1B2]">
                  No late entries in {summary.month}{unit ? ` for ${unit}` : ''} — everyone scores 4 on LATE COMING.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                    <tr><th className="py-2 px-2 text-left">Name</th><th className="py-2 px-2 text-left">Department</th><th className="py-2 px-2 text-left">Unit</th><th className="py-2 px-2 text-center">Late days</th><th className="py-2 px-2 text-center">Total min</th><th className="py-2 px-2 text-center">KPI marks</th></tr>
                  </thead>
                  <tbody>
                    {visibleSummary.map((p) => (
                      <tr key={p.employee_id} className="border-b border-[#F2F4F8]">
                        <td className="py-1.5 px-2 text-[#11182B]">{p.name}</td>
                        <td className="py-1.5 px-2 text-[#5A6377]">{p.department || '—'}</td>
                        <td className="py-1.5 px-2 text-[#5A6377]">{p.unit || '—'}</td>
                        <td className="py-1.5 px-2 text-center font-semibold">{p.late_days}</td>
                        <td className="py-1.5 px-2 text-center">{Math.round(p.late_minutes)}</td>
                        <td className="py-1.5 px-2 text-center">
                          <span className="inline-block min-w-[26px] rounded-md bg-[#E8EFFD] text-[#2563EB] font-bold px-2 py-0.5">{p.marks}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
